import { hasPermission } from './permissions/usePermissions';
import React from 'react';
import { 
  RouterProvider, 
  createHashRouter,
  createRoutesFromElements,
  Route, 
  Navigate,
  Outlet
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ConfigProvider } from 'antd';
import RequirePermission from './permissions/RequirePermission';
// Import all necessary components and pages
import Home from './Pages/home';
import ClientManagementPage from './Pages/client';
import ArticleManagementPage from './Pages/article';
import ComManagementPage from './Pages/com';
import FourManagementPage from './Pages/four';
import DevisManagementPage from './Pages/devis';
import AchatCommandePage from './Pages/achat-commande';
import AchatReceptionPage from './Pages/achat-reception';
import FactureAchatPage from './Pages/facture-achat';
import AvoirAchatPage from './Pages/avoir-achat';
import TransfertPage from './Pages/transfert';
import EntrepotManagementPage from './Pages/entrepot';
import BonlivManagementPage from './Pages/bonliv';
import FactureManagementPage from './Pages/facture';
import AvoirManagementPage from './Pages/avoir';
import RegManagementPage from './Pages/reg';
import FamilleManagementPage from './Pages/famille';
import UserManagementPage from './Pages/user';
import ProfilePage from './Pages/profile';
import NavigationBar from './NavigationBar';
import Sidebar from './Sidebar';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import DashboardPage from './Pages/dashboard';
import DashboardRevenuePage from './Pages/dashboardRevenue';
import BulkUploadPage from './Pages/BulkUploadPage';
import JournalExpManagementPage from './Pages/exped';
import TemplateManagementPage from './Pages/template';
import ListsManagementPage from './Pages/lists';
import PreparationPage from './Pages/preparation';
import PreparationDetail from './components/bonliv/preparation/PreparationDetail';
import AuditPage from './Pages/audit';
import DateTest from './Pages/DateTest';
import InventairePage from './Pages/inventaire';


// App layout wrapper component
const AppLayout = () => {
  // Style objects for the application layout
  const appStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
  };

  const bodyStyle = {
    display: 'flex',
    flex: 1,
  };

  const contentStyle = {
    flexGrow: 1,
    overflow: 'auto',
    background: '#f0f0f0',
    padding: '20px',
  };

  return (
    <div className="App" style={appStyle}>
      <NavigationBar />
      <div className="App-body" style={bodyStyle}>
        <Sidebar />
        <main className="App-content" style={contentStyle}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

function App() {
  const user = useSelector(state => state.user);
  const token = useSelector(state => state.token);
  
  // Enhanced authentication check
  const isAuthenticated = !!user && !!token;

  // Auth check component - redirects ALL non-authenticated users
  const RequireAuth = () => {
    return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
  };

  // Create router with routes
  const router = createHashRouter(
    createRoutesFromElements(
      <Route>
        {/* Login/Register routes */}
        <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
        <Route path="/register" element={isAuthenticated ? <Navigate to="/" /> : <Register />} />
        
        {/* Protected routes */}
        <Route element={<AppLayout />}>
          <Route element={<RequireAuth />}>
            <Route path="/" element={<Home />} />
            
            {/* Profile route - accessible to all authenticated users */}
            <Route path="/profile" element={<ProfilePage />} />
            
            {/* Date Test Page - accessible to all authenticated users */}
            <Route path="/date-test" element={<DateTest />} />
            
            {/* Manager-only routes */}
            <Route path="/user" element={<RequirePermission permission="IsManager" />}>
              <Route index element={<UserManagementPage />} />
            </Route>
            <Route path="/dashboard" element={<RequirePermission permission="operationsDashboard" />}>
              <Route index element={<DashboardPage />} />
            </Route>
            <Route path="/dashboardrevenue" element={<RequirePermission permission="revenueDashboard" />}>
              <Route index element={<DashboardRevenuePage />} />
            </Route>
            <Route path="/audit" element={<RequirePermission permission="audit" />}>
              <Route index element={<AuditPage />} />
              <Route path="logins" element={<AuditPage key="login-history" loginHistory />} />
            </Route>
            
            {/* Bulk Upload Route - a single route for all bulk operations */}
            <Route path="/bulk" element={<BulkUploadPage />} />
            
            {/* Permission-based routes */}
            <Route path="/client" element={<RequirePermission permission="CanManageClients" />}>
              <Route index element={<ClientManagementPage />} />
            </Route>
            <Route path="/article" element={<RequirePermission permission="CanManageArticles" />}>
              <Route index element={<ArticleManagementPage />} />
            </Route>
            <Route path="/com" element={<RequirePermission permission="CanManageCommercials" />}>
              <Route index element={<ComManagementPage />} />
            </Route>
            <Route path="/four" element={<RequirePermission permission="CanManageFournisseurs" />}>
              <Route index element={<FourManagementPage />} />
            </Route>
            {/* ACHAT Modules */}
            <Route path="/achat-commande" element={<RequirePermission permission="CanManageAchatCommande" />}>
              <Route index element={<AchatCommandePage />} />
            </Route>
            <Route path="/achat-reception" element={<RequirePermission permission="CanManageAchatReception" />}>
              <Route index element={<AchatReceptionPage />} />
            </Route>
            <Route path="/facture-achat" element={<RequirePermission permission="CanManageAchatFacture" />}>
              <Route index element={<FactureAchatPage />} />
            </Route>
            <Route path="/avoir-achat" element={<RequirePermission permission="CanManageAchatAvoir" />}>
              <Route index element={<AvoirAchatPage />} />
            </Route>
            <Route path="/transfert" element={<RequirePermission permission="transfers" />}>
              <Route index element={<TransfertPage />} />
            </Route>
            <Route path="/entrepot" element={<RequirePermission permission="warehouses" />}>
              <Route index element={<EntrepotManagementPage />} />
            </Route>
            <Route path="/devis" element={<RequirePermission permission="CanManageQuote" />}>
              <Route index element={<DevisManagementPage />} />
            </Route>
            <Route path="/bonliv" element={<RequirePermission permission="CanManageDeliveryNote" />}>
              <Route index element={<BonlivManagementPage />} />
            </Route>
            <Route path="/preparation" element={
              <RequirePermission permission={user => 
                ['preparation', 'control', 'collection', 'deliveryNotes'].some(id => hasPermission(user, id))
              } />
            }>
              <Route index element={<PreparationPage />} />
            </Route>
            <Route path="/facture" element={<RequirePermission permission="CanManageInvoice" />}>
              <Route index element={<FactureManagementPage />} />
            </Route>
            <Route path="/avoir" element={<RequirePermission permission="CanProcessReturns" />}>
              <Route index element={<AvoirManagementPage />} />
            </Route>
            <Route path="/reg" element={<RequirePermission permission="CanProcessPayments" />}>
              <Route index element={<RegManagementPage />} />
            </Route>
            <Route path="/famille" element={<RequirePermission permission="families" />}>
              <Route index element={<FamilleManagementPage />} />
            </Route>
            <Route path="/inventaire" element={<RequirePermission permission="inventory" />}>
              <Route index element={<InventairePage />} />
            </Route>
            <Route path="/exped" element={<RequirePermission permission="CanManageExpedition" />}>
              <Route index element={<JournalExpManagementPage />} />
            </Route>
            <Route path="/drag" element={<RequirePermission permission="templates" />}>
              <Route index element={<TemplateManagementPage />} />
            </Route>
            <Route path="/lists" element={<RequirePermission permission={user => ['deliveryModes', 'paymentModes', 'countries', 'cities', 'banks'].some(id => hasPermission(user, id))} />}>
              <Route index element={<ListsManagementPage />} />
            </Route>
          </Route>
        </Route>
      </Route>
    )
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Poppins', sans-serif",
          fontFamilyCode: "'Poppins', monospace",
          colorPrimary: '#000',
        }
      }}
    >
      <RouterProvider router={router} />
    </ConfigProvider>
  );
}

export default App;
